import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CityPerformanceRanking = ({ 
  cities = [],
  isLoading = false,
  selectedMetric = 'scans',
  selectedTimeRange = 'last7days',
  selectedCountry = 'all',
  className = '',
  onCityClick
}) => {

  const getChangeIcon = (change) => {
    if (change > 0) return 'TrendingUp';
    if (change < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-success';
    if (change < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getRankStyle = (rank) => {
    if (rank <= 3) return 'bg-primary text-primary-foreground font-bold';
    if (rank <= 5) return 'bg-secondary text-secondary-foreground font-medium';
    return 'bg-muted text-muted-foreground';
  };

  const getMetricValue = (city) => {
    switch (selectedMetric) {
      case 'scans': return city?.scans?.toLocaleString();
      case 'conversions': return city?.conversions?.toLocaleString();
      case 'engagement': return `${city?.conversionRate}%`;
      default: return city?.scans?.toLocaleString();
    }
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-success';
    if (growth < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return 'Smartphone';
      case 'tablet': return 'Tablet';
      case 'desktop': return 'Monitor';
      default: return 'Smartphone';
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-card rounded-lg border border-border ${className}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Trophy" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">City Performance</h3>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={16} className="animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading cities...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!cities || cities?.length === 0) {
    return (
      <div className={`bg-card rounded-lg border border-border ${className}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Trophy" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">City Performance</h3>
          </div>
        </div>
        <div className="p-8 text-center">
          <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-foreground font-medium">No City Data Available</p>
          <p className="text-sm text-muted-foreground mt-2">
            City performance data will appear here once you have QR code scans with location information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg border border-border ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Trophy" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">City Performance</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Top {cities?.length} Cities</span>
        </div>
      </div>

      {/* City Rankings List */}
      <div className="divide-y divide-border">
        {cities?.map((city) => (
          <div
            key={city?.id}
            className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => onCityClick?.(city)}
          >
            <div className="flex items-center justify-between">
              {/* Left Section - Rank & City Info */}
              <div className="flex items-center space-x-3">
                {/* Rank Badge */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${getRankStyle(city?.rank)}`}>
                  {city?.rank}
                </div>

                {/* City & Country Info */}
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{city?.flag}</span>
                  <div>
                    <div className="font-medium text-foreground">{city?.city}</div>
                    <div className="text-xs text-muted-foreground">{city?.country}</div>
                  </div>
                </div>

                {/* Rank Change */}
                {city?.change !== 0 && (
                  <div className={`flex items-center space-x-1 ${getChangeColor(city?.change)}`}>
                    <Icon name={getChangeIcon(city?.change)} size={12} />
                    <span className="text-xs font-medium">
                      {Math.abs(city?.change)}
                    </span>
                  </div>
                )}
              </div>

              {/* Right Section - Metrics */}
              <div className="text-right">
                <div className="font-medium text-foreground">
                  {getMetricValue(city)}
                </div>
                <div className="flex items-center justify-end space-x-2 text-xs text-muted-foreground">
                  <Icon name={getDeviceIcon(city?.topDevice)} size={12} />
                  <span className={getGrowthColor(city?.growth)}>
                    {city?.growth > 0 ? '+' : ''}{city?.growth}%
                  </span>
                </div>
              </div>
            </div>

            {/* Progress Bar for Engagement */}
            <div className="mt-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-muted-foreground">Engagement</span>
                <span className="text-xs font-medium text-foreground">{city?.avgEngagement}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div
                  className="bg-primary h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${city?.avgEngagement}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Showing top {cities?.length} cities</span>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="TrendingUp" size={12} className="text-success" />
              <span>Up</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="TrendingDown" size={12} className="text-error" />
              <span>Down</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Minus" size={12} className="text-muted-foreground" />
              <span>No change</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityPerformanceRanking;